"use strict";

// temporar fix for proper header height
document.body.style.setProperty(
  "--header-height",
  document.querySelector(".js-site-header").getBoundingClientRect().height +
    "px"
);

function debounce({ callback, sleep = 1000, preHook = () => {} }) {
  let timeout;
  return (...args) => {
    preHook.apply(this, args);
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback.apply(this, args);
    }, sleep);
  };
}

function parseHTML(string) {
  return new DOMParser().parseFromString(string, "text/html");
}

class WCDummy extends HTMLElement {
  #tag;
  get tag() {
    return this.#tag;
  }

  constructor({ tag, hasStylesheet }) {
    if (new.target === WCDummy) {
      throw new Error("You should not directly initialize this component");
    }
    super();
    this.#tag = tag;
    this.hasStylesheet = hasStylesheet;
  }

  appendStyles(styles) {
    let style = "";
    if (this.hasStylesheet) {
      let sheet = "";
      try {
        Array.from(
          Array.from(document.styleSheets).find((d) =>
            d.href?.includes(
              `${Shopify.designMode ? `/editor_asset` : `/assets`}/${
                this.tag
              }.css`
            )
          )?.cssRules || []
        ).forEach((d) => {
          sheet += d.cssText;
        });
        style = document.createElement("style");
        style.innerHTML = sheet;
      } catch (_) {
        sheet = document
          .querySelector(
            `link[href*="${Shopify.designMode ? `/editor_asset` : `/assets`}/${
              this.tag
            }.css"]`
          )
          ?.cloneNode(true);
        if (sheet) {
          style = sheet;
        }
      }
    } else if (styles) {
      style = document.createElement("style");
      style.innerHTML = styles;
    }
    return style;
  }
}
const WCLockable = (Class) =>
  class extends Class {
    lockState = [];
    lock({ item, condition }) {
      const itemIndex = this.lockState.findIndex((state) =>
        state.item.isEqualNode(item)
      );
      if (itemIndex >= 0) {
        this.lockState.splice(itemIndex, 1);
      }
      this.lockState.push({ item, condition });
      this.#handleLock.bind(this)();
    }
    release({ item }) {
      this.lockState = this.lockState.filter(
        (state) => !state.item.isEqualNode(item)
      );
      this.#handleLock.bind(this)();
    }

    #handleLock() {
      const state = this.lockState
        .reduce((result, state, array) => {
          const item = result.find((d) => d.id == state.condition.id);
          if (!item) {
            result.push({
              id: state.condition.id,
              size: 1,
              limit: state.condition.limit,
            });
          } else {
            item.size++;
          }
          return result;
        }, [])
        .reduce((result, state) => {
          return result && state.size !== state.limit;
        }, true);
      if (state) {
        this.removeAttribute("lock");
      } else {
        this.setAttribute("lock", "");
      }
    }
  };

const WCObservable = (Class) =>
  class extends Class {
    #observer;
    #observerConfig = {
      once: true,
      options: {
        root: null,
        rootMargin: "0px",
        threshold: 1,
      },
    };
    initObserver({ target = this, thresholdRange = false, ...props } = {}) {
      this.#observerConfig = {
        ...this.#observerConfig,
        ...props,
      };
      this.#observer = new IntersectionObserver(
        this.#handleIntersect.bind(this),
        this.#observerConfig.options
      );
      this.#observer.observe(target);
    }

    #handleIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0) {
          if (this.#observerConfig.once) {
            this.#observer.unobserve(entry.target);
          }
          this.observerCallback && this.observerCallback(entry);
        } else {
        }
      });
    }
  };

const WCOutsideListener = (Class) =>
  class extends Class {
    #outsideClickListener = (event) => {
      const isVisible = () =>
        !!(
          this.offsetWidth ||
          this.offsetHeight ||
          this.getClientRects().length
        );
      if (!this.contains(event.target) && isVisible()) {
        this.outsideListenerCallback && this.outsideListenerCallback(event);
      }
    };
    addOutsideListener() {
      document.addEventListener("click", this.#outsideClickListener);
    }
    removeOutsideListener() {
      document.removeEventListener("click", this.#outsideClickListener);
    }
  };

class WCModule extends WCDummy {
  constructor({ tag, hasStylesheet }) {
    if (new.target === WCModule) {
      throw new Error("You should not directly initialize this component");
    }
    super();
  }
}

class LocalizationProvider {
  static #strings = {};
  static #locale = "";

  constructor() {
    throw new Error("You should not directly initialize this component");
  }

  static register(key, string, locale = this.#locale) {
    `${locale ? `${locale}.` : ""}${key}`.split(".").reduce((a, b, i, arr) => {
      if (!a[b]) {
        a[b] = {};
      }
      if (arr.length === i + 1) {
        a[b] = string;
      }
      return a[b];
    }, this.#strings);
  }
  static get strings() {
    return this.#strings;
  }

  static getString(key, locale = this.#locale) {
    return (
      `${locale ? `${locale}.` : ""}${key}`
        .split(".")
        .reduce((a, b) => a[b] || "", this.#strings) || ""
    );
  }
}

class Animation {
  #animation;
  #node;
  #keyframes;
  #options;
  #onFinish;
  #onCancel;

  constructor() {}

  get animation() {
    return this.#animation;
  }

  set node(node) {
    this.#node = node;
  }

  set keyframes(keyframes) {
    this.#keyframes = keyframes;
  }

  set options(options) {
    this.#options = options;
  }

  set onFinish(onFinish) {
    this.#onFinish = onFinish;
  }

  set onCancel(onCancel) {
    this.#onCancel = onCancel;
  }

  animate() {
    window.requestAnimationFrame(() => {
      if (this.#animation) {
        this.#animation.cancel();
      }
      this.#animation = this.#node.animate(this.#keyframes, this.#options);
      this.#onFinish && (this.#animation.onfinish = this.#onFinish);
      this.onCancel && (this.#animation.onCancel = this.#onCancel);
    });
  }
}

window.AnalyticsLayer = new Proxy(
  {},
  {
    get() {
      return () => {};
    },
  }
);
